import { useMemo, useRef, useState } from 'react'
import { useEditor } from '../store/useEditor'
import { emptyCompany, type Company } from '../lib/company'
import { SaidLine, useSaid } from './bits'

/**
 * THE LETTERHEAD, TYPED ONCE.
 *
 * Everything a homeowner reads at the top of a quote: the mark, the name, how
 * to reach the company, and the license line where a state requires one.
 *
 * WHY THIS IS A DRAFT AND A SAVE BUTTON rather than writing on every
 * keystroke. The store already saves as you type, which is fine in a folded
 * away panel inside a quote and wrong on a screen called Business profile: a
 * half typed phone number is a phone number that goes out on a quote, and
 * there is nothing to press to say "no, wait". So this edits a copy and the
 * copy is committed on Save changes.
 *
 * WHAT IS NOT ON THIS SCREEN, AND IS STILL SAVED. The next document number
 * lives on the same record and is not shown here, because it is not an
 * identity, it is a counter. Saving a patch rather than the whole object is
 * what keeps it from being reset to 1001 by a screen that never displayed it.
 */

const FIELDS: {
  key: keyof Omit<Company, 'logoDataUrl' | 'nextNumber'>
  label: string
  placeholder?: string
  wide?: boolean
  type?: string
}[] = [
  { key: 'name', label: 'Business name', placeholder: 'Your company name', wide: true },
  { key: 'phone', label: 'Phone number', placeholder: '(555) 555-5555' },
  { key: 'email', label: 'Email', placeholder: 'you@yourcompany.com', type: 'email' },
  { key: 'address', label: 'Business address', placeholder: 'Street, city, state, ZIP', wide: true },
  { key: 'prefix', label: 'Document prefix', placeholder: 'LP' },
  { key: 'license', label: 'License, if it has to appear' },
]

export default function Business() {
  const company = useEditor((s) => s.company)
  const setCompany = useEditor((s) => s.setCompany)
  const file = useRef<HTMLInputElement>(null)

  const [draft, setDraft] = useState<Company>(company)
  const { said, say, clear } = useSaid()

  const dirty = useMemo(
    () =>
      (Object.keys(emptyCompany()) as (keyof Company)[]).some((k) => draft[k] !== company[k]),
    [draft, company],
  )

  const set = (patch: Partial<Company>) => {
    setDraft((d) => ({ ...d, ...patch }))
    clear()
  }

  /*
   * A data URL, so the mark survives a reload with no server and no upload,
   * and so it can be drawn straight into a printed document. The contractor's
   * logo never leaves this browser and is never confused with ours: this is
   * his image on his paperwork.
   */
  const pickLogo = (f: File | undefined) => {
    if (!f) return
    if (!/^image\//.test(f.type)) {
      say('That file is not an image.', 'bad')
      return
    }
    if (f.size > 3_000_000) {
      say('That image is over 3 MB. A smaller one prints just as well.', 'bad')
      return
    }
    const r = new FileReader()
    r.onload = () => set({ logoDataUrl: String(r.result) })
    r.onerror = () => say('That image could not be read.', 'bad')
    r.readAsDataURL(f)
  }

  const save = () => {
    try {
      // A patch, never the whole record: nextNumber is not on this screen and
      // must not be written back from a stale copy of it.
      const { logoDataUrl, ...rest } = draft
      setCompany({ ...rest, logoDataUrl })
      say('Saved. It is on your quotes and invoices now.')
    } catch {
      say('That could not be saved on this device.', 'bad')
    }
  }

  const contact = [draft.phone, draft.email, draft.license].filter((x) => x && x.trim()).join('  ·  ')

  return (
    <>
      {/*
        A SECTION HEADING, NOT A SECOND PAGE TITLE. Money & Materials renders
        this and the plan below it, and each was rendering its own h1 with its
        own lede, so one destination carried three page titles down the screen.
        The save lives at the foot of the card it saves, where the fields are.
      */}
      <div className="ws-sec-head">
        <h2>Business profile</h2>
        <p>What a homeowner reads at the top of every quote and invoice you send.</p>
      </div>

      <div className="ws-card">
        <div className="ws-form">
          {/*
            THE MARK, ON THE GROUND IT PRINTS ON. The document band is dark, so
            a preview on white would show a black logo that vanishes in the
            place it actually appears.
          */}
          <div className="ws-logo">
            <div className="ws-logo-box">
              {draft.logoDataUrl ? (
                <img src={draft.logoDataUrl} alt="" />
              ) : (
                <span>{draft.name || 'No image yet'}</span>
              )}
            </div>
            <div className="ws-logo-acts">
              <button className="btn" onClick={() => file.current?.click()}>
                {draft.logoDataUrl ? 'Replace image' : 'Add image'}
              </button>
              {draft.logoDataUrl && (
                <button className="btn" onClick={() => set({ logoDataUrl: null })}>
                  Remove
                </button>
              )}
            </div>
            <p className="hint">
              It prints on a dark band, so a light or transparent image reads best. Nothing is
              cropped.
            </p>
            <input
              ref={file}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                pickLogo(e.target.files?.[0])
                e.target.value = ''
              }}
            />
          </div>

          {FIELDS.map((f) => (
            <label key={f.key} className={f.wide ? 'ws-wide' : undefined}>
              <span>{f.label}</span>
              <input
                type={f.type ?? 'text'}
                value={draft[f.key]}
                placeholder={f.placeholder}
                onChange={(e) => set({ [f.key]: e.target.value } as Partial<Company>)}
              />
            </label>
          ))}

          <div className="ws-wide">
            <p className="ws-eyebrow">How the header will read</p>
            <div className="ws-preview">
              {draft.logoDataUrl ? (
                <img src={draft.logoDataUrl} alt="" />
              ) : (
                <p className="ws-preview-name">{draft.name || 'Your Company'}</p>
              )}
              {contact && <p className="ws-preview-contact">{contact}</p>}
              {draft.address.trim() && <p className="ws-preview-contact">{draft.address}</p>}
            </div>
          </div>
        </div>

        <div className="ws-actions">
          <button className="btn primary" onClick={save} disabled={!dirty}>
            Save changes
          </button>
          <SaidLine said={said} dirty={dirty} />

        </div>
      </div>
    </>
  )
}
